import { useQuery } from '@tanstack/react-query';
import axios, { type AxiosRequestConfig } from 'axios';

import '@api-client'; // side-effect import: registers the axios auth/redirect interceptors
import { env } from '@utils/env';

export interface TransactionBatchAccount {
  id: string;
  accountName: string | null;
  accountNumber: string | null;
  /**
   * Archived accounts keep their batches in this list. A company can hold several
   * accounts with the same name, so without this flag two adjacent rows are
   * indistinguishable [ENG-1211].
   *
   * Optional on purpose: the field ships with easybiz-backend#244, and until that is
   * deployed the response simply has no `isArchived`. Absent reads as "not archived",
   * so nothing is hidden and no marker is drawn. Make it required when this hook is
   * swapped for the generated one (see the TODO below).
   */
  isArchived?: boolean;
}

export interface TransactionBatchBank {
  id: string;
  name: string;
}

/** Where a batch came from: a manual CSV upload, or a bank-integration sync. */
export type TransactionBatchSource = 'csv' | 'integration';

export interface TransactionBatch {
  id: string;
  account: TransactionBatchAccount | null;
  /** Bank behind the account, when the account came from a bank connection. */
  bank: TransactionBatchBank | null;
  source: TransactionBatchSource;
  /** Rows inserted by this batch at import time (from ingest metadata). */
  uploadedTransactionsCount: number;
  /** Rows from this batch currently present in the database. */
  currentTransactionsCount: number;
  /** Earliest/latest booking date across the batch rows currently in the DB. */
  dateFrom: string | null;
  dateTo: string | null;
  createdAt: string;
  /**
   * Signed S3 URL of the source file, or null when there is no file to serve.
   * Integration batches never have one — the sync builds them from the bank API
   * response and uploads nothing, so their rows are the only record of what came in.
   */
  fileUrl: string | null;
}

/** One transaction of a batch, used to reconstruct a preview when there is no source file. */
export interface TransactionBatchRow {
  bookingDate: string;
  amount: number;
  currencyCode: string;
  details: string | null;
  contactName: string | null;
}

const ENDPOINT = `${env.VITE_ROUTE_API_BASE_URL || ''}/api/admin/transaction-ingests`;

export const ADMIN_TRANSACTION_BATCHES_QUERY_KEY = 'adminTransactionBatches';
export const ADMIN_TRANSACTION_BATCH_ROWS_QUERY_KEY = 'adminTransactionBatchRows';

/**
 * The shared @api-client request interceptor rewrites `method` from a custom `httpMethod`
 * field (the generated client always sets it). Without it, `method` is clobbered to
 * `undefined` and the browser's XHR adapter throws before the request is ever sent — so
 * no request reaches the wire.
 */
const getRequestConfig = (
  companyId: string,
  params?: Record<string, unknown>
): AxiosRequestConfig =>
  ({
    headers: { companyId },
    params,
    httpMethod: 'GET',
  }) as AxiosRequestConfig & { httpMethod: string };

/**
 * Uploaded transaction batches (ingests) for the backoffice transactions page.
 *
 * TODO: replace with the generated `useAdminTransactionIngestController_findAll`
 * hook once the backend endpoint is deployed to stage and `yarn api-client:generate`
 * has been run. This manual hook exists so the UI can be built ahead of that deploy;
 * it mirrors the generated client's request shape (companyId header + Bearer token via
 * the shared axios interceptors).
 */
export const useTransactionBatches = (
  companyId?: string,
  { includeEmpty = false }: { includeEmpty?: boolean } = {}
) =>
  useQuery<TransactionBatch[]>({
    queryKey: [ADMIN_TRANSACTION_BATCHES_QUERY_KEY, companyId, includeEmpty],
    queryFn: async () => {
      const response = await axios.get<TransactionBatch[]>(
        ENDPOINT,
        getRequestConfig(companyId as string, { includeEmpty })
      );

      return response.data;
    },
    enabled: Boolean(companyId),
  });

/**
 * The transactions a batch brought in. Only fetched when a batch without a source file is
 * opened, since it stands in for the file that integration batches never have.
 */
export const useTransactionBatchRows = (
  companyId?: string,
  batchId?: string | null
) =>
  useQuery<TransactionBatchRow[]>({
    queryKey: [ADMIN_TRANSACTION_BATCH_ROWS_QUERY_KEY, companyId, batchId],
    queryFn: async () => {
      const response = await axios.get<TransactionBatchRow[]>(
        `${ENDPOINT}/${batchId}/transactions`,
        getRequestConfig(companyId as string)
      );

      return response.data;
    },
    enabled: Boolean(companyId) && Boolean(batchId),
  });
